import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useAppSelector } from '../store/hooks';
import {
  fetchAllUsers,
  updateUserRole,
  deleteComment,
  deleteRating,
  deleteBlogComment,
  checkAdminStatus,
} from '../services/firestore/adminService';
import {
  fetchPendingBlogs,
  approveBlogPost,
  rejectBlogPost,
  fetchAllBlogsAdmin,
} from '../services/firestore/blogService';
import { AdminUser, BlogPost } from '@e-learning/shared/src/store/types';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PeopleIcon from '@mui/icons-material/People';
import ArticleIcon from '@mui/icons-material/Article';
import BarChartIcon from '@mui/icons-material/BarChart';
import CommentIcon from '@mui/icons-material/Comment';
import StarIcon from '@mui/icons-material/Star';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const AdminPanelPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [pendingBlogs, setPendingBlogs] = useState<BlogPost[]>([]);
  const [allBlogs, setAllBlogs] = useState<BlogPost[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; type: string; id: string; courseId?: string }>({
    open: false,
    type: '',
    id: '',
  });

  useEffect(() => {
    checkAdminAccess();
  }, [user, isAuthenticated]);

  const checkAdminAccess = async () => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }

    // First check if user.isAdmin is already set (from auth listener)
    if (user.isAdmin === true) {
      setIsAdmin(true);
      loadData();
      setLoading(false);
      return;
    }

    // Fallback: check admin status from Firestore
    try {
      const admin = await checkAdminStatus(user.uid);
      if (!admin) {
        navigate('/');
        return;
      }
      setIsAdmin(true);
      loadData();
    } catch (error) {
      setError('Failed to verify admin access');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const [usersData, pendingBlogsData, allBlogsData] = await Promise.all([
        fetchAllUsers(),
        fetchPendingBlogs(),
        fetchAllBlogsAdmin(),
      ]);
      setUsers(usersData);
      setPendingBlogs(pendingBlogsData);
      setAllBlogs(allBlogsData);
    } catch (error: any) {
      setError(error.message || 'Failed to load data');
    }
  };

  const handleApproveBlog = async (blogId: string) => {
    try {
      await approveBlogPost(blogId);
      await loadData();
    } catch (error: any) {
      setError(error.message || 'Failed to approve blog');
    }
  };

  const handleRejectBlog = async (blogId: string) => {
    try {
      await rejectBlogPost(blogId);
      await loadData();
    } catch (error: any) {
      setError(error.message || 'Failed to reject blog');
    }
  };

  const handleDelete = async () => {
    try {
      const { type, id, courseId } = deleteDialog;
      if (type === 'comment' && courseId) {
        await deleteComment(id, courseId);
      } else if (type === 'rating') {
        await deleteRating(id, courseId || '');
      } else if (type === 'blogComment') {
        await deleteBlogComment(id, courseId || '');
      }
      setDeleteDialog({ open: false, type: '', id: '' });
      await loadData();
    } catch (error: any) {
      setError(error.message || 'Failed to delete');
    }
  };

  const handleUpdateUserRole = async (userId: string, role: 'admin' | 'user') => {
    try {
      await updateUserRole(userId, role);
      await loadData();
    } catch (error: any) {
      setError(error.message || 'Failed to update user role');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <AdminPanelSettingsIcon sx={{ fontSize: 48, color: 'primary.main' }} />
          <Typography variant="h3" sx={{ fontWeight: 700 }}>
            Admin Panel
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage users, approve blogs, and monitor platform activity
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(148, 163, 184, 0.1)' }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{
            borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
            },
          }}
        >
          <Tab icon={<PeopleIcon />} iconPosition="start" label="Users" />
          <Tab icon={<ArticleIcon />} iconPosition="start" label="Blogs" />
          <Tab icon={<BarChartIcon />} iconPosition="start" label="Analytics" />
          <Tab icon={<CommentIcon />} iconPosition="start" label="Comments" />
          <Tab icon={<StarIcon />} iconPosition="start" label="Ratings" />
        </Tabs>

        {/* Users Tab */}
        <TabPanel value={tabValue} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.uid}>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.displayName || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.role}
                        color={user.role === 'admin' ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={user.role}
                          onChange={(e) => handleUpdateUserRole(user.uid, e.target.value as 'admin' | 'user')}
                        >
                          <MenuItem value="user">User</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Blogs Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Pending Approval ({pendingBlogs.length})
            </Typography>
            <Grid container spacing={2}>
              {pendingBlogs.map((blog) => (
                <Grid item xs={12} key={blog.id}>
                  <Card sx={{ background: 'rgba(15, 23, 42, 0.5)' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <Box>
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            {blog.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {blog.excerpt}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <Chip label={blog.category} size="small" />
                            <Typography variant="caption" color="text.secondary">
                              By: {blog.authorName}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => handleApproveBlog(blog.id)}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<CancelIcon />}
                            onClick={() => handleRejectBlog(blog.id)}
                          >
                            Reject
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              All Blogs ({allBlogs.length})
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Author</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Views</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Likes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allBlogs.map((blog) => (
                    <TableRow key={blog.id}>
                      <TableCell>{blog.title}</TableCell>
                      <TableCell>{blog.authorName}</TableCell>
                      <TableCell>
                        <Chip
                          label={blog.approved ? 'Approved' : 'Pending'}
                          color={blog.approved ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{blog.views}</TableCell>
                      <TableCell>{blog.likes}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Analytics Tab */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ background: 'rgba(15, 23, 42, 0.5)', textAlign: 'center', p: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {users.length}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Total Users
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ background: 'rgba(15, 23, 42, 0.5)', textAlign: 'center', p: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {allBlogs.length}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Total Blogs
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ background: 'rgba(15, 23, 42, 0.5)', textAlign: 'center', p: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {pendingBlogs.length}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Pending Blogs
                </Typography>
              </Card>
            </Grid>
          </Grid>
          <Box sx={{ mt: 4 }}>
            <Alert severity="info">
              Full analytics integration with Firebase Analytics and Google Analytics coming soon. Check your Firebase Console for detailed metrics.
            </Alert>
          </Box>
        </TabPanel>

        {/* Comments Tab */}
        <TabPanel value={tabValue} index={3}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Comment management interface coming soon. Use the course detail pages to manage comments.
          </Alert>
        </TabPanel>

        {/* Ratings Tab */}
        <TabPanel value={tabValue} index={4}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Rating management interface coming soon. Use the course detail pages to manage ratings.
          </Alert>
        </TabPanel>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, type: '', id: '' })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this {deleteDialog.type}? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, type: '', id: '' })}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminPanelPage;

